import React, { useEffect, useMemo, useRef } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L, { LatLngExpression } from 'leaflet';
import { Device } from '../../types/Device';
import { MAP_TILE_URL } from '../../const/const';
import { LEAFLET_ICONS } from 'utils/app/assets/images/leaflet';
import { isPointInKazakhstan } from '../../utils/kazakhstanBounds';

interface LocationMapProps {
    devices: Device[];
}

delete L.Icon.Default.imagePath;

const icon = L.icon({
    iconUrl: LEAFLET_ICONS.markerIcon,
    iconRetinaUrl: LEAFLET_ICONS.markerIconRetina,
    shadowUrl: LEAFLET_ICONS.markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

function getBounds(devices: Device[]): [[number, number], [number, number]] | null {
    if (devices.length === 0) return null;
    const lats = devices.map((d) => d.latitude);
    const lngs = devices.map((d) => d.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    return [
        [minLat, minLng],
        [maxLat, maxLng],
    ];
}

const FitBounds: React.FC<{ bounds: [[number, number], [number, number]] }> = ({ bounds }) => {
    const map = useMap();
    useEffect(() => {
        map.fitBounds(bounds, { padding: [40, 40] });
    }, [bounds, map]);
    return null;
};

const LocationMap2: React.FC<LocationMapProps> = ({ devices }) => {
    const mapRef = useRef<L.Map | null>(null);

    const validDevices = useMemo(() => {
        return (
            devices?.filter(
                (device) =>
                    device.latitude !== 0 &&
                    device.longitude !== 0 &&
                    !isNaN(device.latitude) &&
                    !isNaN(device.longitude) &&
                    isPointInKazakhstan(device.latitude, device.longitude),
            ) || []
        );
    }, [devices]);

    const centerCoords: LatLngExpression = useMemo(() => {
        if (validDevices.length === 0) {
            return [48.0196, 66.9237];
        }

        const sum = validDevices.reduce(
            (acc, device) => {
                acc.lat += device.latitude;
                acc.lng += device.longitude;
                return acc;
            },
            { lat: 0, lng: 0 },
        );

        return [sum.lat / validDevices.length, sum.lng / validDevices.length];
    }, [validDevices]);

    const bounds = useMemo(() => getBounds(validDevices), [validDevices]);

    if (!validDevices || validDevices.length === 0) {
        return (
            <div
                style={{
                    height: 'calc(100vh - 176px)',
                    width: '480px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '5px',
                    backgroundColor: '#f0f0f0',
                    marginRight: 24,
                }}
            >
                Нет устройств с допустимыми координатами для отображения
            </div>
        );
    }

    return (
        <div className="LocationMap">
            <MapContainer
                ref={mapRef}
                center={centerCoords}
                zoom={10}
                style={{
                    height: 'calc(100vh - 176px)',
                    width: '480px',
                    borderRadius: '5px',
                    zIndex: 0,
                    position: 'relative',
                    marginRight: 24,
                }}
                scrollWheelZoom={true}
            >
                <TileLayer
                // attribution={`&copy; <a href="${MAP_TILE_URL}">Local Server Map</a>`}
                // url={`${MAP_TILE_URL}/{z}/{x}/{y}.png`}
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url={MAP_TILE_URL}
                    subdomains={['a', 'b', 'c']}
                />
                {bounds && <FitBounds bounds={bounds} />}
                {validDevices
                    .filter((device: Device) => isPointInKazakhstan(device.latitude, device.longitude))
                    .map((device: Device) => (
                    <Marker
                        key={device.UID}
                        position={[device.latitude, device.longitude]}
                        icon={icon}
                    >
                        <Popup>
                            <div>
                                <p>
                                    <strong>Название устройства:</strong> {device.name}
                                </p>
                                <p>
                                    <strong>Описание устройства:</strong> {device.description}
                                </p>
                                <p>
                                    <strong>Модель устройства:</strong> {device.model}
                                </p>
                            </div>
                            {device.online && <p style={{ color: 'green' }}>Онлайн</p>}
                            {!device.online && <p style={{ color: 'red' }}>Оффлайн</p>}
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};

export default LocationMap2;
